import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { X, Shapes, Type, Eye, EyeOff, Copy } from "lucide-react";
import { getTemplatePositions } from "@/config/templatePositions";
import { SymbolSelector } from "@/components/SymbolSelector";

interface Symbol {
  id: string;
  name: string;
  meaning: string;
  image: string;
  category: "adinkra" | "other";
}

interface TemplateCustomizerProps {
  selectedPattern: string;
  onAddSymbol: (
    position: "left" | "right",
    symbol: Symbol,
    useInscriptionPosition?: boolean
  ) => void;
  onAddInscription: (
    position: "left" | "right",
    text: string,
    useLogoPosition?: boolean
  ) => void;
  onRemoveSymbol: (position: "left" | "right") => void;
  onRemoveInscription: (position: "left" | "right") => void;
  showPositioningGuides?: boolean;
  onTogglePositioningGuides?: (show: boolean) => void;
  onExportPositions?: (positions: any) => void;
  isAdmin?: boolean;
}

interface SelectedSymbol {
  left?: Symbol;
  right?: Symbol;
}

interface InscriptionText {
  left?: string;
  right?: string;
}

const TemplateCustomizer = ({
  selectedPattern,
  onAddSymbol,
  onAddInscription,
  onRemoveSymbol,
  onRemoveInscription,
  showPositioningGuides = false,
  onTogglePositioningGuides,
  onExportPositions,
  isAdmin = false,
}: TemplateCustomizerProps) => {
  const [selectedSymbols, setSelectedSymbols] = useState<SelectedSymbol>({});
  const [inscriptions, setInscriptions] = useState<InscriptionText>({});
  const [activeTab, setActiveTab] = useState<"left" | "right">("left");
  const [showSymbolSelector, setShowSymbolSelector] = useState<
    "left" | "right" | null
  >(null);
  const [selectorType, setSelectorType] = useState<"logo" | "text" | null>(
    null
  );

  const positions = getTemplatePositions(selectedPattern);

  const handleSymbolSelect = (position: "left" | "right", symbol: Symbol) => {
    setSelectedSymbols((prev) => ({ ...prev, [position]: symbol }));

    // Determine if symbol should go to logo position or inscription position
    if (selectorType === "logo") {
      // Add symbol to logo position (useInscriptionPosition = false)
      onAddSymbol(position, symbol, false);
    } else {
      // Add symbol to inscription position (useInscriptionPosition = true)
      onAddSymbol(position, symbol, true);
    }

    setShowSymbolSelector(null);
    setSelectorType(null);
  };

  const handleInscriptionChange = (
    position: "left" | "right",
    text: string
  ) => {
    setInscriptions((prev) => ({ ...prev, [position]: text }));
    if (text.trim()) {
      onAddInscription(position, text, false); // false = useInscriptionPosition
    } else {
      onRemoveInscription(position);
    }
  };

  const handleLogoTextChange = (position: "left" | "right", text: string) => {
    // Add text to the logo position instead of inscription position
    setInscriptions((prev) => ({ ...prev, [position]: text }));
    if (text.trim()) {
      onAddInscription(position, text, true); // true = useLogoPosition
    } else {
      onRemoveInscription(position);
    }
  };

  const handleRemoveSymbol = (position: "left" | "right") => {
    setSelectedSymbols((prev) => {
      const newSymbols = { ...prev };
      delete newSymbols[position];
      return newSymbols;
    });
    onRemoveSymbol(position);
  };

  const handleRemoveInscription = (position: "left" | "right") => {
    setInscriptions((prev) => {
      const newInscriptions = { ...prev };
      delete newInscriptions[position];
      return newInscriptions;
    });
    onRemoveInscription(position);
  };

  const handleClearPosition = (position: "left" | "right") => {
    // Remove both symbol and inscription for this position
    if (selectedSymbols[position]) {
      handleRemoveSymbol(position);
    }
    if (inscriptions[position]) {
      handleRemoveInscription(position);
    }
  };

  const handleExportPositions = () => {
    if (!onExportPositions) return;

    // Get all positioning guides from canvas
    const canvas = document.querySelector("canvas");
    if (!canvas) return;

    // This will be handled by the parent component
    onExportPositions(selectedPattern);
  };

  const copyPositionValues = (position: any, type: string, side: string) => {
    if (!position) return;

    const values = `X: ${position.x}, Y: ${position.y}, W: ${position.width}, H: ${position.height}`;
    const code = `{ x: ${position.x}, y: ${position.y}, width: ${
      position.width
    }, height: ${position.height}${
      type === "inscription" ? `, fontSize: ${position.fontSize || 24}` : ""
    } }`;

    navigator.clipboard
      .writeText(code)
      .then(() => {
        alert(
          `✅ ${side.toUpperCase()} ${type.toUpperCase()} position copied!\n\n${values}\n\n📋 Code: ${code}`
        );
      })
      .catch(() => {
        alert(
          `${side.toUpperCase()} ${type.toUpperCase()} position:\n\n${values}`
        );
      });
  };

  return (
    <Card className="w-full overflow-visible">
      <CardHeader className="overflow-visible pb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="space-y-2">
            <CardTitle className="flex items-center gap-2 text-lg sm:text-xl font-bold text-gray-900">
              <div className="w-2 h-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"></div>
              <Shapes className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
              <span className="hidden sm:inline">Template Customization</span>
              <span className="sm:hidden">Customize</span>
            </CardTitle>
            <p className="text-sm text-gray-600 leading-relaxed">
              <span className="hidden sm:inline">
                Customize your sash with symbols and text. Click any position to
                add your preferred content.
              </span>
              <span className="sm:hidden">
                Add symbols & text to customize your sash
              </span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            {onTogglePositioningGuides && (
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  onTogglePositioningGuides(!showPositioningGuides)
                }
                disabled={!isAdmin}
                className={`flex items-center justify-center gap-2 transition-all duration-200 ${
                  !isAdmin
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-blue-50 hover:border-blue-300"
                }`}
                title={!isAdmin ? "Admin access required to show guides" : ""}
              >
                {showPositioningGuides ? (
                  <>
                    <EyeOff className="h-4 w-4 text-gray-600" />
                    <span className="hidden sm:inline text-gray-700">
                      Hide Guides
                    </span>
                    <span className="sm:hidden text-gray-700">Hide</span>
                  </>
                ) : (
                  <>
                    <Eye className="h-4 w-4 text-gray-600" />
                    <span className="hidden sm:inline text-gray-700">
                      Show Guides
                    </span>
                    <span className="sm:hidden text-gray-700">Show</span>
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="overflow-visible">
        {/* Desktop: Professional responsive layout */}
        <div className="hidden lg:block overflow-visible">
          <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 via-purple-50 to-indigo-50 rounded-xl border border-blue-200 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-3 h-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"></div>
              <p className="text-sm font-semibold text-gray-800">
                Flexible Placement
              </p>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed">
              Choose symbols or text for each position. Mix and match as needed
              for your unique design.
            </p>
          </div>

          {/* Professional Grid Layout */}
          <div className="space-y-6 overflow-visible">
            {/* Left Side Row */}
            <div className="space-y-4 overflow-visible">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full shadow-sm"></div>
                <h3 className="text-base font-semibold text-gray-800">
                  Left Side
                </h3>
              </div>
              <div className="grid grid-cols-2 gap-4 overflow-visible">
                {/* Left Logo */}
                <div className="relative overflow-visible">
                  <Button
                    onClick={() => {
                      setShowSymbolSelector("left");
                      setSelectorType("logo");
                    }}
                    className="h-24 flex flex-col items-center justify-center gap-3 bg-white hover:bg-blue-50 text-gray-700 border-2 border-gray-200 hover:border-blue-400 transition-all duration-300 rounded-xl shadow-sm hover:shadow-md group"
                  >
                    <div className="relative">
                      <Shapes className="h-7 w-7 text-blue-600 group-hover:text-blue-700 transition-colors" />
                      {selectedSymbols.left && (
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white shadow-sm"></div>
                      )}
                    </div>
                    <span className="text-sm font-semibold">Logo</span>
                  </Button>
                </div>

                {/* Left Text */}
                <div className="relative overflow-visible">
                  <Button
                    onClick={() => {
                      setShowSymbolSelector("left");
                      setSelectorType("text");
                    }}
                    className="h-24 flex flex-col items-center justify-center gap-3 bg-white hover:bg-purple-50 text-gray-700 border-2 border-gray-200 hover:border-purple-400 transition-all duration-300 rounded-xl shadow-sm hover:shadow-md group"
                  >
                    <div className="relative">
                      <Type className="h-7 w-7 text-purple-600 group-hover:text-purple-700 transition-colors" />
                      {inscriptions.left && (
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white shadow-sm"></div>
                      )}
                    </div>
                    <span className="text-sm font-semibold">Text</span>
                  </Button>
                </div>
              </div>
            </div>

            {/* Right Side Row */}
            <div className="space-y-4 overflow-visible">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full shadow-sm"></div>
                <h3 className="text-base font-semibold text-gray-800">
                  Right Side
                </h3>
              </div>
              <div className="grid grid-cols-2 gap-4 overflow-visible">
                {/* Right Logo */}
                <div className="relative overflow-visible">
                  <Button
                    onClick={() => {
                      setShowSymbolSelector("right");
                      setSelectorType("logo");
                    }}
                    className="h-24 flex flex-col items-center justify-center gap-3 bg-white hover:bg-green-50 text-gray-700 border-2 border-gray-200 hover:border-green-400 transition-all duration-300 rounded-xl shadow-sm hover:shadow-md group"
                  >
                    <div className="relative">
                      <Shapes className="h-7 w-7 text-green-600 group-hover:text-green-700 transition-colors" />
                      {selectedSymbols.right && (
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white shadow-sm"></div>
                      )}
                    </div>
                    <span className="text-sm font-semibold">Logo</span>
                  </Button>
                </div>

                {/* Right Text */}
                <div className="relative overflow-visible">
                  <Button
                    onClick={() => {
                      setShowSymbolSelector("right");
                      setSelectorType("text");
                    }}
                    className="h-24 flex flex-col items-center justify-center gap-3 bg-white hover:bg-orange-50 text-gray-700 border-2 border-gray-200 hover:border-orange-400 transition-all duration-300 rounded-xl shadow-sm hover:shadow-md group"
                  >
                    <div className="relative">
                      <Type className="h-7 w-7 text-orange-600 group-hover:text-orange-700 transition-colors" />
                      {inscriptions.right && (
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white shadow-sm"></div>
                      )}
                    </div>
                    <span className="text-sm font-semibold">Text</span>
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Show current selections */}
          {(selectedSymbols.left ||
            selectedSymbols.right ||
            inscriptions.left ||
            inscriptions.right) && (
            <div className="mt-8 p-5 bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl border border-gray-200 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-2 h-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"></div>
                <h4 className="text-base font-semibold text-gray-800">
                  Current Selections
                </h4>
              </div>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span className="font-medium text-gray-700">Left Side</span>
                  </div>
                  <div className="space-y-2">
                    {selectedSymbols.left && (
                      <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-center gap-2">
                          <Shapes className="h-4 w-4 text-blue-600" />
                          <img
                            src={selectedSymbols.left.image}
                            alt=""
                            className="w-6 h-6"
                          />
                        </div>
                        <span className="text-blue-700 font-medium flex-1">
                          {selectedSymbols.left.name}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveSymbol("left")}
                          className="h-7 w-7 p-0 hover:bg-red-50 hover:text-red-600 transition-colors"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                    {inscriptions.left && (
                      <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                        <Type className="h-4 w-4 text-purple-600" />
                        <span className="text-purple-700 font-medium flex-1">
                          "{inscriptions.left}"
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveInscription("left")}
                          className="h-7 w-7 p-0 hover:bg-red-50 hover:text-red-600 transition-colors"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="font-medium text-gray-700">
                      Right Side
                    </span>
                  </div>
                  <div className="space-y-2">
                    {selectedSymbols.right && (
                      <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-center gap-2">
                          <Shapes className="h-4 w-4 text-green-600" />
                          <img
                            src={selectedSymbols.right.image}
                            alt=""
                            className="w-6 h-6"
                          />
                        </div>
                        <span className="text-green-700 font-medium flex-1">
                          {selectedSymbols.right.name}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveSymbol("right")}
                          className="h-7 w-7 p-0 hover:bg-red-50 hover:text-red-600 transition-colors"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                    {inscriptions.right && (
                      <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                        <Type className="h-4 w-4 text-orange-600" />
                        <span className="text-orange-700 font-medium flex-1">
                          "{inscriptions.right}"
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveInscription("right")}
                          className="h-7 w-7 p-0 hover:bg-red-50 hover:text-red-600 transition-colors"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Mobile: Show detailed tabs */}
        <div className="lg:hidden">
          <Tabs
            value={activeTab}
            onValueChange={(value) => setActiveTab(value as "left" | "right")}
          >
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger
                value="left"
                className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
              >
                <div className="w-2 h-2 sm:w-3 sm:h-3 bg-blue-500 rounded-full"></div>
                <span className="hidden sm:inline">Left Side</span>
                <span className="sm:hidden">Left</span>
              </TabsTrigger>
              <TabsTrigger
                value="right"
                className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
              >
                <div className="w-2 h-2 sm:w-3 sm:h-3 bg-green-500 rounded-full"></div>
                <span className="hidden sm:inline">Right Side</span>
                <span className="sm:hidden">Right</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="left" className="space-y-4">
              <div className="space-y-4">
                {/* Left Symbol Selection */}
                <div className="space-y-2">
                  <Label htmlFor="left-symbol">Left Symbol</Label>
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setShowSymbolSelector("left")}
                      className="flex-1"
                    >
                      <Shapes className="mr-2 h-4 w-4" />
                      <span className="hidden sm:inline">
                        {selectedSymbols.left
                          ? "Change Symbol"
                          : "Select Symbol"}
                      </span>
                      <span className="sm:hidden">
                        {selectedSymbols.left ? "Change" : "Select"}
                      </span>
                    </Button>
                    {selectedSymbols.left && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveSymbol("left")}
                        className="flex items-center justify-center"
                      >
                        <X className="h-4 w-4" />
                        <span className="hidden sm:inline ml-1">Remove</span>
                      </Button>
                    )}
                  </div>
                  {selectedSymbols.left && (
                    <div className="flex items-center gap-2 p-2 bg-muted rounded">
                      <img
                        src={selectedSymbols.left.image}
                        alt={selectedSymbols.left.name}
                        className="w-6 h-6"
                      />
                      <div>
                        <p className="text-sm font-medium">
                          {selectedSymbols.left.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {selectedSymbols.left.meaning}
                        </p>
                      </div>
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground bg-muted p-2 rounded">
                    <div className="flex items-center justify-between">
                      <div>
                        <strong>Position:</strong> {positions.left.logo?.x},{" "}
                        {positions.left.logo?.y}
                        <br />
                        <strong>Size:</strong> {positions.left.logo?.width}×
                        {positions.left.logo?.height}px
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          copyPositionValues(
                            positions.left.logo,
                            "logo",
                            "left"
                          )
                        }
                        className="h-6 w-6 p-0"
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Left Inscription */}
                <div className="space-y-2">
                  <Label htmlFor="left-inscription">Left Inscription</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="left-inscription"
                      placeholder="e.g., Class of 2025"
                      value={inscriptions.left || ""}
                      onChange={(e) =>
                        handleInscriptionChange("left", e.target.value)
                      }
                      className="flex-1"
                    />
                    {inscriptions.left && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveInscription("left")}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground bg-muted p-2 rounded">
                    <div className="flex items-center justify-between">
                      <div>
                        <strong>Position:</strong>{" "}
                        {positions.left.inscription?.x},{" "}
                        {positions.left.inscription?.y}
                        <br />
                        <strong>Size:</strong>{" "}
                        {positions.left.inscription?.width}×
                        {positions.left.inscription?.height}px
                        <br />
                        <strong>Font Size:</strong>{" "}
                        {positions.left.inscription?.fontSize}px
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          copyPositionValues(
                            positions.left.inscription,
                            "inscription",
                            "left"
                          )
                        }
                        className="h-6 w-6 p-0"
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="right" className="space-y-4">
              <div className="space-y-4">
                {/* Right Symbol Selection */}
                <div className="space-y-2">
                  <Label htmlFor="right-symbol">Right Symbol</Label>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setShowSymbolSelector("right")}
                      className="flex-1"
                    >
                      <Shapes className="mr-2 h-4 w-4" />
                      {selectedSymbols.right
                        ? "Change Symbol"
                        : "Select Symbol"}
                    </Button>
                    {selectedSymbols.right && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveSymbol("right")}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  {selectedSymbols.right && (
                    <div className="flex items-center gap-2 p-2 bg-muted rounded">
                      <img
                        src={selectedSymbols.right.image}
                        alt={selectedSymbols.right.name}
                        className="w-6 h-6"
                      />
                      <div>
                        <p className="text-sm font-medium">
                          {selectedSymbols.right.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {selectedSymbols.right.meaning}
                        </p>
                      </div>
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground bg-muted p-2 rounded">
                    <div className="flex items-center justify-between">
                      <div>
                        <strong>Position:</strong> {positions.right.logo?.x},{" "}
                        {positions.right.logo?.y}
                        <br />
                        <strong>Size:</strong> {positions.right.logo?.width}×
                        {positions.right.logo?.height}px
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          copyPositionValues(
                            positions.right.logo,
                            "logo",
                            "right"
                          )
                        }
                        className="h-6 w-6 p-0"
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Right Inscription */}
                <div className="space-y-2">
                  <Label htmlFor="right-inscription">Right Inscription</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="right-inscription"
                      placeholder="e.g., University Name"
                      value={inscriptions.right || ""}
                      onChange={(e) =>
                        handleInscriptionChange("right", e.target.value)
                      }
                      className="flex-1"
                    />
                    {inscriptions.right && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveInscription("right")}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground bg-muted p-2 rounded">
                    <div className="flex items-center justify-between">
                      <div>
                        <strong>Position:</strong>{" "}
                        {positions.right.inscription?.x},{" "}
                        {positions.right.inscription?.y}
                        <br />
                        <strong>Size:</strong>{" "}
                        {positions.right.inscription?.width}×
                        {positions.right.inscription?.height}px
                        <br />
                        <strong>Font Size:</strong>{" "}
                        {positions.right.inscription?.fontSize}px
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          copyPositionValues(
                            positions.right.inscription,
                            "inscription",
                            "right"
                          )
                        }
                        className="h-6 w-6 p-0"
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          {/* Preview Info */}
          <div className="mt-6 p-4 bg-muted rounded-lg">
            <h4 className="font-medium mb-2">Template: {selectedPattern}</h4>
            <div className="grid grid-cols-2 gap-4 text-xs">
              <div>
                <strong>Left Side:</strong>
                <ul className="mt-1 space-y-1">
                  <li>
                    • Logo:{" "}
                    {positions.left.logo ? "Available" : "Not available"}
                  </li>
                  <li>
                    • Inscription:{" "}
                    {positions.left.inscription ? "Available" : "Not available"}
                  </li>
                </ul>
              </div>
              <div>
                <strong>Right Side:</strong>
                <ul className="mt-1 space-y-1">
                  <li>
                    • Logo:{" "}
                    {positions.right.logo ? "Available" : "Not available"}
                  </li>
                  <li>
                    • Inscription:{" "}
                    {positions.right.inscription
                      ? "Available"
                      : "Not available"}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </CardContent>

      {/* Symbol Selector Modal */}
      {showSymbolSelector && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center p-4">
          <div className="bg-background rounded-t-lg max-w-4xl w-full max-h-[85vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"></div>
                <h3 className="text-xl font-bold text-gray-900">
                  Customize {showSymbolSelector} Side
                </h3>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSymbolSelector(null)}
                className="h-8 w-8 p-0 hover:bg-gray-100 rounded-full"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[75vh]">
              <SymbolSelector
                onAddSymbol={(symbol) =>
                  handleSymbolSelect(showSymbolSelector, symbol)
                }
                onAddText={(text) => {
                  if (selectorType === "logo") {
                    handleLogoTextChange(showSymbolSelector, text);
                  } else {
                    handleInscriptionChange(showSymbolSelector, text);
                  }
                  setShowSymbolSelector(null);
                  setSelectorType(null);
                }}
                onClose={() => {
                  setShowSymbolSelector(null);
                  setSelectorType(null);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default TemplateCustomizer;
